/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * Licensed under The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import { useCallback, useRef, useState } from 'preact/hooks';
import { getRandomId } from '#utils/PRIVATE_visLayoutUtils';
import { cancelEvent } from '#utils/UNSAFE_visUtils';
import { useVisHover } from '#hooks/PRIVATE_useVisHover';
import { mergeProps } from '#utils/UNSAFE_mergeProps';
import { gestureTypes } from '#hooks/PRIVATE_useVisContextMenu';
import {
  ItemDrillDetail,
  ItemFocusDetail,
  ItemHoverDetail,
  PieChartItemData
} from '#UNSAFE_PieChart/pieChart.types';
import { useVisDrill } from '#hooks/PRIVATE_useVisDrill';

export type ItemInfo = {
  isFocusVisible?: boolean;
  itemIndex: number;
};

export const getItemInfo = (event: Event) => {
  const itemIndex = (event.target as HTMLElement).dataset['ojPrivateItemIndex'];
  if (itemIndex) {
    return {
      itemIndex: parseInt(itemIndex, 10)
    };
  }
  return;
};

/**
 * Hook to handle chart navigation. This can be shared between the pie, funnel and pyramid chart
 * as they follow similar navigation pattern.
 * TODO: rename
 */
export function usePieChartNav<K, D extends PieChartItemData<K>>(
  touchResponse: 'touchStart' | 'touchHold',
  getDetailFromInfo: (info?: ItemInfo) => ItemHoverDetail<K, D> | ItemFocusDetail<K, D>,
  getNextChartItem: (
    info: ItemInfo,
    arrow: 'ArrowLeft' | 'ArrowRight' | 'ArrowDown' | 'ArrowUp'
  ) => ItemInfo,
  drilling?: 'on' | 'off',
  onItemDrill?: (detail: ItemDrillDetail<K, D>) => void
) {
  const [focusedItemInfo, setfocusedItemInfo] = useState<ItemInfo>({ itemIndex: 0 });
  const [hoveredItemInfo, setHoveredItemInfo] = useState<ItemInfo>();
  const activeId = useRef<string>();

  const onContextMenuDismissed = (gesture: gestureTypes) => {
    if (gesture === 'keyboard') {
      updateFocusedItemInfo({
        ...focusedItemInfo,
        isFocusVisible: true
      });
    }
  };

  const updateFocusedItemInfo = (itemInfo: ItemInfo) => {
    if (
      itemInfo.itemIndex !== focusedItemInfo.itemIndex ||
      itemInfo.isFocusVisible !== focusedItemInfo.isFocusVisible
    ) {
      setfocusedItemInfo(itemInfo);
      setHoveredItemInfo(undefined);
    }
  };

  const onPointerUp = (event: PointerEvent) => {
    const info = getItemInfo(event);
    if (info) {
      updateFocusedItemInfo({ ...info, isFocusVisible: false });
    }
  };

  const onHover = (event: PointerEvent) => {
    const info = getItemInfo(event);
    if (info?.itemIndex != hoveredItemInfo?.itemIndex) {
      setHoveredItemInfo(info);
      activeId.current = getRandomId();
    } else if (!info) {
      activeId.current = undefined;
    }
  };

  const onHoverLeave = () => {
    setHoveredItemInfo(undefined);
    activeId.current = undefined;
  };

  const hoverHandlers = useVisHover(onHover, undefined, onHoverLeave, touchResponse);
  const processDrill = useCallback(
    (info?: ItemInfo) => {
      const { id, data } = getDetailFromInfo(info);
      if (id != null && data) {
        onItemDrill?.({ id, data });
        activeId.current = getRandomId();
      }
    },
    [getDetailFromInfo, onItemDrill]
  );

  const onDrill = useCallback(
    (event: Event) => {
      if (drilling == 'on') {
        updateFocusedItemInfo({
          ...focusedItemInfo,
          isFocusVisible: true
        });
        if (event.type === 'keydown') {
          processDrill(focusedItemInfo);
        } else {
          processDrill(getItemInfo(event));
        }
      }
    },
    [drilling, focusedItemInfo, processDrill, updateFocusedItemInfo]
  );

  const drillProps = useVisDrill('none', onDrill);

  const onKeyDown = (event: KeyboardEvent) => {
    const key = event.key;
    switch (key) {
      case 'Tab':
        return;
      case 'ArrowDown': {
        const info = getNextChartItem(focusedItemInfo, 'ArrowDown');
        updateFocusedItemInfo({ ...info, isFocusVisible: true });
        cancelEvent(event);
        break;
      }
      case 'ArrowUp': {
        const info = getNextChartItem(focusedItemInfo, 'ArrowUp');
        updateFocusedItemInfo({ ...info, isFocusVisible: true });
        cancelEvent(event);
        break;
      }
      case 'ArrowLeft': {
        const info = getNextChartItem(focusedItemInfo, 'ArrowLeft');
        updateFocusedItemInfo({ ...info, isFocusVisible: true });
        cancelEvent(event);
        break;
      }
      case 'ArrowRight': {
        const info = getNextChartItem(focusedItemInfo, 'ArrowRight');
        updateFocusedItemInfo({ ...info, isFocusVisible: true });
        cancelEvent(event);
        break;
      }
    }
    activeId.current = getRandomId();
  };

  const onKeyUp = (event: KeyboardEvent) => {
    const key = event.key;
    if (key === 'Tab') {
      updateFocusedItemInfo({ ...focusedItemInfo, isFocusVisible: true });
    }
  };

  const onBlur = () => {
    updateFocusedItemInfo({
      ...focusedItemInfo,
      isFocusVisible: false
    });
  };

  return {
    focusedItemInfo,
    hoveredItemInfo,
    activeId: activeId.current,
    eventsProps: mergeProps(drillProps, hoverHandlers, {
      onKeyDown,
      onKeyUp,
      onPointerUp,
      onBlur
    }),
    onContextMenuDismissed
  };
}
